/**
 * Tenant Admin tab visibility for Pending Users, Registered Users, and Manage Roles.
 *
 * Stored on the role named exactly "admin" as an attached policy. A missing policy
 * keeps today's behavior (tabs visible). An explicit eye off hides that tab.
 * Platform admins always see the tabs.
 */

import type { AttachedPolicyEntry } from '../services/api/types'

export const ADMIN_PANEL_SERVICE = 'admin-panel-feature-access'

export const ADMIN_PANEL_CARDS = [
  { id: 'pending-users', service: 'pending_users', label: 'Pending Users' },
  { id: 'registered-users', service: 'registered_users', label: 'Registered Users' },
  { id: 'manage-roles', service: 'manage_roles', label: 'Manage Roles' },
] as const

export type AdminPanelTabId = (typeof ADMIN_PANEL_CARDS)[number]['id']
export type AdminPanelService = (typeof ADMIN_PANEL_CARDS)[number]['service']
export type AdminPanelEyes = Record<AdminPanelService, boolean>

const ADMIN_PANEL_TAB_IDS = new Set<string>(ADMIN_PANEL_CARDS.map((card) => card.id))

export interface AdminPanelPolicyNode {
  service?: string
  actions?: Record<string, boolean>
  view_actions?: boolean
  children?: AdminPanelPolicyNode[]
}

export interface AdminPanelRoleLike {
  name?: string | null
  attached_policies?: AdminPanelPolicyNode[] | null
}

export function adminPanelEyesAll(on: boolean): AdminPanelEyes {
  return {
    pending_users: on,
    registered_users: on,
    manage_roles: on,
  }
}

export function isTenantAdminRoleName(roleName: string): boolean {
  return roleName.trim().toLowerCase().replace(/[\s-]/g, '_') === 'admin'
}

export function isAdminPanelTabId(tabId: string): tabId is AdminPanelTabId {
  return ADMIN_PANEL_TAB_IDS.has(tabId)
}

function serviceForTab(tabId: AdminPanelTabId): AdminPanelService {
  const card = ADMIN_PANEL_CARDS.find((item) => item.id === tabId)
  return card?.service ?? 'pending_users'
}

function childVisible(node: AdminPanelPolicyNode | undefined): boolean | null {
  if (!node) return null
  if (node.view_actions === true || node.view_actions === false) return node.view_actions
  if (node.actions && 'view' in node.actions) return Boolean(node.actions.view)
  return null
}

function roleTabVisible(role: AdminPanelRoleLike, service: AdminPanelService): boolean | null {
  const policies = role.attached_policies ?? []
  const root = policies.find((entry) => entry?.service === ADMIN_PANEL_SERVICE)
  if (!root) return null
  const child = (root.children ?? []).find((entry) => entry?.service === service)
  return childVisible(child)
}

/**
 * Whether this user should see an Admin tab.
 * Platform admins always can. Other roles follow the tenant Admin role's eye.
 * No stored eye means visible, so existing Admin roles keep the tabs until turned off.
 */
export function isAdminPanelTabVisible(
  roles: readonly AdminPanelRoleLike[] | null | undefined,
  tabId: AdminPanelTabId,
  isPlatformAdmin: boolean,
): boolean {
  if (isPlatformAdmin) return true
  const service = serviceForTab(tabId)
  const adminRoles = (roles ?? []).filter((role) => isTenantAdminRoleName(role.name ?? ''))
  if (adminRoles.length === 0) return false
  return adminRoles.some((role) => roleTabVisible(role, service) !== false)
}

export function readAdminPanelEyes(policies: AdminPanelPolicyNode[] | null | undefined): {
  eyes: AdminPanelEyes
  stored: boolean
} {
  const root = (policies ?? []).find((entry) => entry?.service === ADMIN_PANEL_SERVICE)
  if (!root) {
    return { eyes: adminPanelEyesAll(true), stored: false }
  }
  const eyes = adminPanelEyesAll(true)
  for (const card of ADMIN_PANEL_CARDS) {
    const child = (root.children ?? []).find((entry) => entry?.service === card.service)
    const visible = childVisible(child)
    if (visible !== null) eyes[card.service] = visible
  }
  return { eyes, stored: true }
}

function adminPanelPolicy(eyes: AdminPanelEyes): AttachedPolicyEntry {
  const anyOn = ADMIN_PANEL_CARDS.some((card) => eyes[card.service])
  return {
    service: ADMIN_PANEL_SERVICE,
    actions: { view: anyOn },
    view_actions: anyOn,
    children: ADMIN_PANEL_CARDS.map((card) => ({
      service: card.service,
      actions: { view: eyes[card.service] },
      view_actions: eyes[card.service],
    })),
  }
}

/**
 * Write the Admin-panel eyes onto a role save payload.
 * Only the role named admin keeps the policy. Untouched legacy admins omit it
 * so opening the page is not a change.
 */
export function mergeAdminPanelPolicy(
  policies: AttachedPolicyEntry[],
  roleName: string,
  eyes: AdminPanelEyes,
  stored: boolean,
): AttachedPolicyEntry[] {
  const rest = policies.filter((entry) => entry.service !== ADMIN_PANEL_SERVICE)
  if (!isTenantAdminRoleName(roleName)) return rest
  const allOn = ADMIN_PANEL_CARDS.every((card) => eyes[card.service])
  if (!stored && allOn) return rest
  return [...rest, adminPanelPolicy(eyes)]
}
